import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useChatSocket } from "@/hooks/useChatSocket";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, MessageCircle, Lock, MapPin, Wifi, WifiOff } from "lucide-react";

// Browser Notification API push
async function showPushNotification(sender, text) {
  if (typeof Notification === "undefined") return;
  if (document.visibilityState === "visible" && document.hasFocus()) return;
  let perm = Notification.permission;
  if (perm === "default") {
    try { perm = await Notification.requestPermission(); } catch (_e) { return; }
  }
  if (perm !== "granted") return;
  try {
    const n = new Notification(`${sender?.name || "A neighbour"} on MyLiveFriend`, {
      body: text?.length > 100 ? text.slice(0, 97) + "..." : text,
      icon: sender?.picture || "/favicon.ico",
      tag: `rento-${sender?.user_id || "msg"}`,
    });
    n.onclick = () => { window.focus(); if (sender?.user_id) window.location.href = `/chat/${sender.user_id}`; };
  } catch (_e) {}
}

export default function Chat() {
  const { user } = useAuth();
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [partner, setPartner] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [locked, setLocked] = useState(false);
  const [live, setLive] = useState(false);
  const [typing, setTyping] = useState(false);
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const wsSendRef = useRef(null);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const r = await api.get("/chat/threads");
      setThreads(r.data.threads || []);
    } catch (e) {
      if (e.response?.status === 402) setLocked(true);
    }
  }, []);

  const loadMessages = useCallback(async () => {
    if (!partnerId) return;
    try {
      const r = await api.get(`/chat/messages/${partnerId}`);
      setMessages(r.data.messages || []);
      setPartner(r.data.partner);
    } catch (e) {
      if (e.response?.status === 402) setLocked(true);
    }
  }, [partnerId]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  // WebSocket live push
  useChatSocket(useCallback((data, send) => {
    if (send) wsSendRef.current = send;
    if (data.type === "ready") { setLive(true); return; }
    if (data.type === "typing") {
      if (partnerId && data.from === partnerId) {
        setTyping(true);
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTyping(false), 2500);
      }
      return;
    }
    if (data.type !== "message") return;
    const m = data.message;
    const relevant = partnerId && (m.from_user_id === partnerId || m.to_user_id === partnerId);
    if (relevant) {
      setMessages((prev) => prev.some((x) => x.message_id === m.message_id) ? prev : [...prev, m]);
      if (m.from_user_id === partnerId) setTyping(false);
    }
    // Browser push notification when received from someone else
    if (m.from_user_id !== user?.user_id) {
      const isActiveChat = partnerId === m.from_user_id && document.visibilityState === "visible";
      if (!isActiveChat) showPushNotification(data.sender, m.text);
    }
    loadThreads();
  }, [partnerId, loadThreads, user]));

  const emitTyping = () => {
    const now = Date.now();
    if (!partnerId || !wsSendRef.current) return;
    if (now - lastTypingSentRef.current < 1500) return;
    lastTypingSentRef.current = now;
    try { wsSendRef.current({ type: "typing", to: partnerId }); } catch (_e) {}
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || !partnerId) return;
    setSending(true);
    try {
      await api.post("/chat/send", { to_user_id: partnerId, text });
      setInput("");
      await loadMessages();
      await loadThreads();
    } finally {
      setSending(false);
    }
  };

  if (locked) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <main className="max-w-xl mx-auto px-6 py-20 text-center">
          <Lock className="w-10 h-10 mx-auto text-slate-400" />
          <h2 className="font-heading font-bold text-2xl mt-4">Chat is locked</h2>
          <p className="text-slate-600 mt-2">Upgrade to a Pro plan to message neighbours in your pincode.</p>
          <Button data-testid="chat-upgrade-btn" onClick={() => navigate("/plans")} className="mt-6 bg-amber-500 hover:bg-amber-600 text-white rounded-full">See plans</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <AppHeader />
      <div className="flex-1 max-w-6xl w-full mx-auto px-0 sm:px-6 py-0 sm:py-6 flex" style={{ minHeight: "calc(100vh - 72px)" }}>
        <aside className={`${partnerId ? "hidden md:flex" : "flex"} w-full md:w-80 flex-col bg-white border border-slate-200 md:rounded-l-2xl`}>
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-heading font-bold text-lg">Chats</h2>
            <p className="text-xs text-slate-500">Your conversations</p>
          </div>
          <div className="flex-1 overflow-y-auto chat-scroll" data-testid="chat-threads-list">
            {threads.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                <MessageCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                No chats yet. Start one from Discover.
              </div>
            ) : threads.map((t) => (
              <button
                data-testid={`chat-thread-item-${t.partner.user_id}`}
                key={t.thread_id}
                onClick={() => navigate(`/chat/${t.partner.user_id}`)}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 border-b border-slate-100 ${partnerId === t.partner.user_id ? "bg-teal-50" : ""}`}
              >
                {t.partner.picture ? (
                  <img src={t.partner.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-amber-400 text-white flex items-center justify-center font-semibold">
                    {t.partner.name?.[0] || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{t.partner.name}</div>
                  <div className="text-xs text-slate-500 truncate">{t.last_text}</div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        <section className={`${partnerId ? "flex" : "hidden md:flex"} flex-1 flex-col bg-slate-50 md:bg-white border border-slate-200 border-l-0 md:rounded-r-2xl`} data-testid="chat-window">
          {!partnerId ? (
            <div className="flex-1 flex items-center justify-center text-slate-500">
              <div className="text-center">
                <MessageCircle className="w-10 h-10 mx-auto text-slate-300" />
                <p className="mt-3">Select a chat or start a new one from Discover.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-slate-200 bg-white flex items-center gap-3">
                <button className="md:hidden text-sm text-teal-700 font-medium" onClick={() => navigate("/chat")}>← Back</button>
                {partner?.picture ? (
                  <img src={partner.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center font-semibold">{partner?.name?.[0] || "?"}</div>
                )}
                <div>
                  <div className="font-heading font-bold flex items-center gap-2">
                    {partner?.name || "Loading..."}
                    <VerifiedBadge user={partner} />
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {partner?.area}, {partner?.city} · PIN {partner?.pincode}</span>
                    <span data-testid="ws-status" className={`inline-flex items-center gap-1 ${live ? "text-emerald-600" : "text-slate-400"}`}>
                      {live ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                      {live ? "Live" : "Connecting"}
                    </span>
                  </div>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll p-4 space-y-3 bg-slate-50" data-testid="chat-messages">
                {messages.length === 0 && (
                  <div className="text-center text-sm text-slate-400 mt-10">Say hi 👋 to start the conversation.</div>
                )}
                {messages.map((m) => {
                  const mine = m.from_user_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div data-testid={`chat-msg-${m.message_id}`} className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm shadow-sm ${mine ? "bg-teal-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm"}`}>
                        <div>{m.text}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-teal-100" : "text-slate-400"}`}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {typing && (
                  <div className="flex justify-start" data-testid="typing-indicator">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-2 shadow-sm flex items-center gap-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                      <span className="ml-2 text-xs text-slate-500">{partner?.name?.split(" ")[0] || "neighbour"} is typing...</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-3 border-t border-slate-200 bg-white flex items-center gap-2">
                <Input
                  data-testid="chat-message-input"
                  value={input}
                  onChange={(e) => { setInput(e.target.value); emitTyping(); }}
                  onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                  placeholder="Type a message..."
                  className="flex-1"
                />
                <Button data-testid="chat-send-button" onClick={send} disabled={sending || !input.trim()} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-5">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
